import { ARTICLES } from './domains/ARTICLES';
import { CERTIFICATIONS } from './domains/CERTIFICATIONS';
import { COURSES } from './domains/COURSES';
import { EDUCATION } from './domains/EDUCATION';
import { HONORS } from './domains/HONORS';
import { LANGUAGES } from './domains/LANGUAGES';
import { ORGANIZATIONS } from './domains/ORGANIZATIONS';
import { PATENTS } from './domains/PATENTS';
import { PHONE_NUMBERS } from './domains/PHONE_NUMBERS';
import { POSITIONS } from './domains/POSITIONS';
import { PROJECTS } from './domains/PROJECTS';
import { PUBLICATIONS } from './domains/PUBLICATIONS';
import { SKILLS } from './domains/SKILLS';
import { buildMinimalSchema } from './auto-schema';
import type { DomainSchema } from './types';

// ─── Full domain name list ────────────────────────────────────────────────────
// All known LinkedIn Member Snapshot domains. Declared first so it can be
// used below when building the combined registry.

export const ALL_DOMAIN_NAMES = [
  'ACCOUNT_HISTORY',
  'ACTOR_SAVE_ITEM',
  'ADS_CLICKED',
  'ADS_LAN',
  'AD_TARGETING',
  'ALL_COMMENTS',
  'ALL_LIKES',
  'ALL_VOTES',
  'ARTICLES',
  'CAUSES_YOU_CARE_ABOUT',
  'CERTIFICATIONS',
  'COMPANY_FOLLOWS',
  'CONNECTIONS',
  'CONTACTS',
  'COURSES',
  'EASYAPPLY_BLOCKING',
  'EDUCATION',
  'EMAIL_ADDRESSES',
  'ENDORSEMENTS',
  'EVENTS',
  'GROUPS',
  'HONORS',
  'IDENTITY_CREDENTIALS_AND_ASSETS',
  'INBOX',
  'INFERENCE_TAKEOUT',
  'INSTANT_REPOSTS',
  'INVITATIONS',
  'JOB_APPLICANT_SAVED_ANSWERS',
  'JOB_APPLICATIONS',
  'JOB_POSTINGS',
  'JOB_SEEKER_PREFERENCES',
  'LANGUAGES',
  'LEARNING',
  'LEARNING_COACH_AI_TAKEOUT',
  'LEARNING_COACH_INBOX',
  'LEARNING_ROLEPLAY_INBOX',
  'LOGIN',
  'MARKETPLACE_ENGAGEMENTS',
  'MARKETPLACE_OPPORTUNITIES',
  'MARKETPLACE_PROVIDERS',
  'MEMBER_FOLLOWING',
  'MEMBER_SHARE_INFO',
  'ORGANIZATIONS',
  'PATENTS',
  'PHONE_NUMBERS',
  'POSITIONS',
  'PROFILE',
  'PROFILE_SUMMARY',
  'PROJECTS',
  'PUBLICATIONS',
  'RECEIPTS',
  'RECEIPTS_LBP',
  'RECOMMENDATIONS',
  'REGISTRATION',
  'REVIEWS',
  'RICH_MEDIA',
  'SAVED_JOBS',
  'SAVED_JOB_ALERTS',
  'SEARCHES',
  'SECURITY_CHALLENGE_PIPE',
  'SKILLS',
  'TALENT_QUESTION_SAVED_RESPONSE',
  'TEST_SCORES',
  'TRUSTED_GRAPH',
  'VOLUNTEERING_EXPERIENCES',
] as const;

export type SnapshotDomainName = (typeof ALL_DOMAIN_NAMES)[number];

// ─── Priority schemas (hand-crafted, priority: true) ─────────────────────────
// The 13 curated domains used for CV parsing and profile UI rendering.

const PRIORITY_SCHEMAS = {
  POSITIONS,
  EDUCATION,
  HONORS,
  PATENTS,
  ARTICLES,
  PUBLICATIONS,
  PROJECTS,
  COURSES,
  CERTIFICATIONS,
  ORGANIZATIONS,
  LANGUAGES,
  PHONE_NUMBERS,
  SKILLS,
} as const;

export type PriorityDomainName = keyof typeof PRIORITY_SCHEMAS;

// ─── Full domain registry (all 64 LinkedIn snapshot domains) ─────────────────
// Non-priority schemas are auto-generated from domain-structures.json with
// minimal field types and no curated layout. Priority schemas override any
// auto-generated entry for the same domain name.

const nonPrioritySchemas: Record<string, DomainSchema> = {};
for (const name of ALL_DOMAIN_NAMES) {
  if (!(name in PRIORITY_SCHEMAS)) {
    const schema = buildMinimalSchema(name);
    if (schema) nonPrioritySchemas[name] = schema;
  }
}

export const ALL_DOMAIN_REGISTRY: Record<string, DomainSchema> = {
  ...nonPrioritySchemas,
  ...PRIORITY_SCHEMAS,
};

// ─── Priority registry (filtered view) ───────────────────────────────────────
// Contains only the 13 curated domains. Use this for CV parsing and UI rendering.

export const PRIORITY_DOMAIN_REGISTRY: typeof PRIORITY_SCHEMAS = PRIORITY_SCHEMAS;

export const PRIORITY_DOMAIN_NAMES = Object.keys(PRIORITY_DOMAIN_REGISTRY) as PriorityDomainName[];

// ─── Guard helpers ────────────────────────────────────────────────────────────

export function isSnapshotDomain(value: string): value is SnapshotDomainName {
  return (ALL_DOMAIN_NAMES as readonly string[]).includes(value);
}

export function isPriorityDomain(value: string): value is PriorityDomainName {
  return value in PRIORITY_DOMAIN_REGISTRY;
}

// ─── Normalisation aliases ────────────────────────────────────────────────────
// Maps canonical field names to alternate spellings seen in raw LinkedIn exports
// and CV parser output. Used by the API for domain record sanitisation.

export const NORMALIZATION_ALIASES: Record<string, string[]> = {
  startDate: ['Start Date', 'Started On', 'StartDate', 'start_date'],
  endDate: ['End Date', 'Finished On', 'EndDate', 'end_date'],
  degreeName: ['Degree', 'Degree Name', 'DegreeName'],
  schoolName: ['School Name', 'Institution', 'SchoolName'],
  companyName: ['Company Name', 'Company', 'CompanyName'],
  recommendationBody: ['Recommendation', 'Recommendation Body', 'recommendationBody'],
};
